import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import "@abinitio/ace-builds/src-noconflict/ace";
import { configureAceModules } from "abreact/incubator/ui/codeeditor/AceModuleConfiguration";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import * as React from "react";
import * as ReactDOM from "react-dom";
/**
 * Basic syntax highlighting code editor/renderer.
 * This component has some nice features like collapsible regions, validation, undo, and
 * with a little prodding, can provide generic autocompletion support for some language types.
 * This component is based on the 3rd party open source Ace code editor
 * https://ace.c9.io/#nav=about
 * Out of the box, it support a large number of code types (see mode property below).
 */
export class AbCodeEditor extends BaseRCmp {
  constructor(props) {
    super(props);
    _defineProperty(this, "_editor", null);
    _defineProperty(this, "machineNeedsUpdate", false);
    _defineProperty(this, "modeNeedsUpdate", false);
    _defineProperty(this, "optionsNeedsUpdate", false);
    _defineProperty(this, "readonlyNeedsUpdate", false);
    _defineProperty(this, "lineWrapNeedsUpdate", false);
    _defineProperty(this, "timeoutId", -1);
    AbCodeEditor.initializeAce();
  }
  componentDidMount() {
    let divRoot = ReactDOM.findDOMNode(this);
    this._editor = ace.edit(divRoot);
    this.setDefaultEditorProperties();
    let value = this.props.machine.value;
    let cursorPos = this.props.machine.cursorPos;
    this._editor.setValue(value, cursorPos);
    this._editor.setReadOnly(this.props.readonly);
    this._editor.getSession().setUseWrapMode(this.props.lineWrap);

    // Set friend field
    let m = this.props.machine;
    m.aceEditor = this._editor;

    // if (this.hasEventListener(AbCodeEditorCmp.CHANGE))
    // {
    //   this.registerForEditorChange();
    // }
  }
  destroy() {
    if (this._editor) {
      this._editor.destroy();
      this._editor = null;
    }
    if (this.timeoutId > 0) {
      clearTimeout(this.timeoutId);
      this.timeoutId = -1;
    }
    return super.destroy();
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.mode !== this.props.mode) {
      this.modeNeedsUpdate = true;
    }
    if (!ObjectUtils.deepCompare(nextProps.options, this.props.options)) {
      this.optionsNeedsUpdate = true;
    }
    if (nextProps.machine !== this.props.machine) {
      this.machineNeedsUpdate = true;
    }
    if (nextProps.lineWrap !== this.props.lineWrap) {
      this.lineWrapNeedsUpdate = true;
    }
    if (nextProps.readonly !== this.props.readonly) {
      this.readonlyNeedsUpdate = true;
    }
    if (nextProps.width !== this.props.width || nextProps.height !== this.props.height) {
      this.setResizeTimer();
    }
  }
  render() {
    let {
      width,
      height
    } = this.props;
    let className = "ab-code-editor";
    if (this.props.className) {
      className += " " + this.props.className;
    }
    let style = {
      width,
      height,
      ...this.props.style
    };
    if (this.machineNeedsUpdate) {
      this.updateMachine();
    }
    if (this.modeNeedsUpdate) {
      this.updateMode();
    }
    if (this.optionsNeedsUpdate) {
      this.updateOptions();
    }
    if (this.lineWrapNeedsUpdate) {
      this.updateLineWrap();
    }
    if (this.readonlyNeedsUpdate) {
      this.updateReadonly();
    }
    return /*#__PURE__*/React.createElement("div", {
      className: className,
      style: style
    });
  }
  setDefaultEditorProperties() {
    if (this._editor == null) {
      return;
    }
    this.updateMode();
    this.updateOptions();
    this._editor.$blockScrolling = Infinity;
    this._editor.setShowPrintMargin(false);
  }
  updateMachine() {
    if (this._editor) {
      let newValue = this.props.machine.value;
      let m = this.props.machine;
      m.aceEditor = this._editor;

      // If cursorPos is undefined, then ace selects all.  If it's a number, ace sets the selection.  We want neither.
      this._editor.setValue(newValue, true);
      this.machineNeedsUpdate = false;
    }
  }
  updateMode() {
    if (this._editor && !StringUtils.isNullOrEmpty(this.props.mode)) {
      this._editor.session.setMode("ace/mode/" + this.props.mode.toLowerCase());
      this.modeNeedsUpdate = false;
    }
  }
  updateOptions() {
    if (this._editor) {
      this._editor.setOptions({
        tabSize: 2,
        useSoftTabs: true,
        showInvisibles: this.props.options?.showInvisibleCharacters,
        showGutter: this.props.options?.showGutter ?? true
      });
      this._editor.setTheme(this.props.options?.theme ?? "ace/theme/ab-initio");
      this.optionsNeedsUpdate = false;
    }
  }
  updateReadonly() {
    if (this._editor) {
      this._editor.setReadOnly(this.props.readonly);
      this.readonlyNeedsUpdate = false;
    }
  }
  updateLineWrap() {
    if (this._editor) {
      this._editor.getSession().setUseWrapMode(this.props.lineWrap);
      this.lineWrapNeedsUpdate = false;
    }
  }

  // This code was originally in AbCodeEditorCmp, but it needed by React and Gelato-flavored code editors,
  // so it's now in the React component where it can shared.
  static initializeAce() {
    // This needs to be put into some central place and done just once per app
    if (AbCodeEditor.aceInitialized) {
      return;
    }

    // configure ace module paths
    configureAceModules(ace);

    // The import statement I use at the top seems to be equivalent to this
    // a.config.loadModule("ace/ext/language_tools");
    // ace.require("ace/ext/language_tools");

    AbCodeEditor.aceInitialized = true;
  }

  /**
   * The Ace Editor does not deal very well with wrapper div size changes, so this method is called anytime a width/height
   * prop is changed. This sets a timer to kick the Ace Editor to resize.
   */
  setResizeTimer() {
    if (this.timeoutId > 0) {
      // Timer already set
      return;
    }
    this.timeoutId = setTimeout(() => {
      this.timeoutId = -1;
      this.resizeEditor();
    }, 100);
  }
  resizeEditor() {
    if (this._editor) {
      this._editor.resize();
    }
  }
}
_defineProperty(AbCodeEditor, "aceInitialized", false);
_defineProperty(AbCodeEditor, "appRootBase", "");
_defineProperty(AbCodeEditor, "defaultProps", {
  lineWrap: false
});
//# sourceMappingURL=AbCodeEditor.js.map
